import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MIN_ZOOM, MAX_ZOOM, DEFAULT_ZOOM, ZOOM_SENSITIVITY, FIT_PADDING,
} from '@/components/facility/constants';

export interface Bounds {
  minX: number; minY: number; maxX: number; maxY: number;
}

interface Options {
  mode: 'scroll' | 'transform';
  minZoom?: number;
  maxZoom?: number;
  initialZoom?: number;
}

export function useViewportCamera(opts: Options) {
  const { mode, minZoom = MIN_ZOOM, maxZoom = MAX_ZOOM, initialZoom = DEFAULT_ZOOM } = opts;

  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(initialZoom);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null);

  // ─── wheel zoom (cursor-centered) ──────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const delta = -e.deltaY * ZOOM_SENSITIVITY;

      setZoom(prev => {
        const next = Math.min(maxZoom, Math.max(minZoom, prev + delta * prev));
        if (mode === 'transform') {
          setOffset(o => ({
            x: cx - (cx - o.x) * (next / prev),
            y: cy - (cy - o.y) * (next / prev),
          }));
        }
        return next;
      });
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [mode, minZoom, maxZoom]);

  // ─── pointer drag-to-pan ───────────────────────────
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Only pan with middle mouse or when no bay is targeted
    if (e.button !== 1 && e.button !== 0) return;
    // Let bay objects handle their own pointer events
    if ((e.target as HTMLElement).closest('[data-bay-object]')) return;

    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [offset]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setOffset({ x: dragStart.current.ox + dx, y: dragStart.current.oy + dy });
  }, []);

  const onPointerUp = useCallback(() => {
    dragStart.current = null;
    setIsDragging(false);
  }, []);

  // ─── touch pinch-to-zoom ───────────────────────────
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
      pinchStart.current = { dist, zoom };
    }
  }, [zoom]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStart.current) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
      const ratio = dist / pinchStart.current.dist;
      setZoom(Math.min(maxZoom, Math.max(minZoom, pinchStart.current.zoom * ratio)));
    }
  }, [minZoom, maxZoom]);

  const onTouchEnd = useCallback(() => {
    pinchStart.current = null;
  }, []);

  // ─── fit / reset ───────────────────────────────────
  const resetZoom = useCallback(() => {
    setZoom(DEFAULT_ZOOM);
    setOffset({ x: 0, y: 0 });
  }, []);

  const fitToContent = useCallback((bounds: Bounds) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const contentW = bounds.maxX - bounds.minX;
    const contentH = bounds.maxY - bounds.minY;
    if (contentW === 0 || contentH === 0) return;

    const scaleX = (rect.width - FIT_PADDING * 2) / contentW;
    const scaleY = (rect.height - FIT_PADDING * 2) / contentH;
    const newZoom = Math.min(maxZoom, Math.max(minZoom, Math.min(scaleX, scaleY)));

    const cx = (bounds.minX + bounds.maxX) / 2;
    const cy = (bounds.minY + bounds.maxY) / 2;

    setZoom(newZoom);
    setOffset({
      x: rect.width / 2 - cx * newZoom,
      y: rect.height / 2 - cy * newZoom,
    });
  }, [minZoom, maxZoom]);

  // ─── transform style ──────────────────────────────
  const getTransformStyle = useCallback((): React.CSSProperties => ({
    transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
    transformOrigin: '0 0',
  }), [zoom, offset]);

  return {
    containerRef,
    zoom,
    offset,
    isDragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
    fitToContent,
    resetZoom,
    getTransformStyle,
  };
}
